import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { QUALIFICATION_FIELDS, type DealInput, type DealDetailRow } from "../schemas/deal";
import type { DealPersonInput, DealPersonRow } from "../schemas/dealPerson";
import type { DealCompetitorInput, DealCompetitorRow } from "../schemas/dealCompetitor";

export const dealKeys = {
  all: ["deals"] as const,
  details: () => [...dealKeys.all, "detail"] as const,
  detail: (id: string) => [...dealKeys.details(), id] as const,
  qualification: (id: string) => ["deal_qualification", id] as const,
  persons: (id: string) => ["deal_persons", id] as const,
  competitors: (id: string) => ["deal_competitors", id] as const,
};

export function useDeal(id: string | undefined) {
  return useQuery({
    queryKey: dealKeys.detail(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .schema("manna")
        .from("deals")
        .select(
          `
            id, name, pipeline_id, stage_id,
            amount, currency,
            expected_close_date, actual_close_date,
            lost_reason, won_reason, tags, probability_pct_override,
            primary_person_id, customer_organization_id, owner_user_id,
            ae_user_id, bdr_user_id, territory_id, campaign_id, lead_source_id,
            priority,
            budget_amount, budget_currency, budget_confidence, budget_notes,
            current_problems, desired_outcomes,
            compelling_event_date, compelling_event_driver, urgency,
            decision_process,
            row_version, created_at, updated_at, created_by, updated_by,
            pipeline:pipelines(id, name),
            stage:pipeline_stages(id, name, stage_type, qualification_phase)
          `,
        )
        .eq("id", id!)
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as unknown as DealDetailRow;

      const [personRes, orgRes, ownerRes] = await Promise.all([
        row.primary_person_id
          ? supabase
              .schema("platform")
              .from("persons")
              .select("id, full_name")
              .eq("id", row.primary_person_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        row.customer_organization_id
          ? supabase
              .schema("platform")
              .from("organizations")
              .select("id, name")
              .eq("id", row.customer_organization_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        row.owner_user_id
          ? supabase
              .schema("platform")
              .from("profiles")
              .select("id, email, avatar_url, person:persons(full_name)")
              .eq("id", row.owner_user_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);

      row.primary_person = (personRes.data as DealDetailRow["primary_person"]) ?? null;
      row.customer_organization = (orgRes.data as DealDetailRow["customer_organization"]) ?? null;
      row.owner = (ownerRes.data as DealDetailRow["owner"]) ?? null;

      // Resolve created_by / updated_by to display names for the read-only
      // Created / Updated info shown on the detail form.
      const actorIds = Array.from(
        new Set([row.created_by, row.updated_by].filter((id): id is string => Boolean(id))),
      );
      const nameById = new Map<string, string>();
      if (actorIds.length > 0) {
        const { data: actors } = await supabase
          .schema("platform")
          .from("profiles")
          .select("id, email, person:persons(full_name)")
          .in("id", actorIds);
        for (const a of (actors ?? []) as Array<{
          id: string;
          email: string | null;
          person: { full_name: string | null } | null;
        }>) {
          nameById.set(a.id, a.person?.full_name || a.email || "");
        }
      }
      row.created_by_name = row.created_by ? (nameById.get(row.created_by) ?? null) : null;
      row.updated_by_name = row.updated_by ? (nameById.get(row.updated_by) ?? null) : null;
      return row;
    },
  });
}

// Filters out the qualification fields — those go to a different RPC.
function stripQualificationFields<T extends Partial<DealInput>>(input: T): Partial<DealInput> {
  const copy: Record<string, unknown> = { ...input };
  for (const key of QUALIFICATION_FIELDS) {
    delete copy[key];
  }
  return copy as Partial<DealInput>;
}

export function useUpdateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DealInput & { id: string; row_version: number }) => {
      // Per UI design §4.2.2: non-qualification fields go to the existing
      // manna_update_deal RPC. Qualification fields are routed through the
      // dedicated manna_update_deal_qualification RPC (see
      // useUpdateDealQualification below).
      const payload = stripQualificationFields(input);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)("manna_update_deal", { payload });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: dealKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: dealKeys.qualification(variables.id) });
      qc.invalidateQueries({ queryKey: ["data_view", "manna", "deal"] });
    },
  });
}

// ── Deal team assignment (territory / AE / BDR / owner) ──────────────────────
//
// The "Deal Team" rail section edits one field at a time. manna_update_deal
// respects `payload ? 'key'`, so a partial payload patches only the supplied
// field; the RPC still enforces the manager / revenue_leader gates and the
// row_version optimistic lock. Kept separate from useUpdateDeal so callers
// don't have to assemble a full DealInput.
export interface DealAssignmentInput {
  id: string;
  row_version: number;
  territory_id?: string | null;
  ae_user_id?: string | null;
  bdr_user_id?: string | null;
  owner_user_id?: string | null;
}

export function useUpdateDealAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DealAssignmentInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)("manna_update_deal", { payload: input });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: dealKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: ["data_view", "manna", "deal"] });
    },
  });
}

// ── Stage transitions (UI design §4.5) ──────────────────────────────────────
//
// Stage changes route through the guarded `manna_update_deal_stage` RPC
// (security audit R4-SB2 — never use the unguarded `manna_update_deal`).
// The user flow is preview → conditional confirm dialog → guarded write.

export interface PreviewDealStageChangeInput {
  dealId: string;
  newStageId: string;
}

export interface PreviewDealStageChangeResult {
  missing: string[];
  new_phase: "qualify" | "discover" | "close" | null;
  will_advance_phase: boolean;
}

export function usePreviewDealStageChange() {
  return useMutation({
    mutationFn: async (
      input: PreviewDealStageChangeInput,
    ): Promise<PreviewDealStageChangeResult> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("preview_deal_stage_change", {
        p_deal_id: input.dealId,
        p_new_stage_id: input.newStageId,
      });
      if (error) throw error;
      // Postgres `returns table` is an array of rows — usually just 1
      const row = Array.isArray(data) ? data[0] : data;
      return (row ?? {
        missing: [],
        new_phase: null,
        will_advance_phase: false,
      }) as PreviewDealStageChangeResult;
    },
  });
}

export interface UpdateDealStageInput {
  dealId: string;
  newStageId: string;
  rowVersion: number;
  wonReason?: string | null;
  lostReason?: string | null;
}

export function useUpdateDealStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateDealStageInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("manna_update_deal_stage", {
        p_id: input.dealId,
        p_new_stage_id: input.newStageId,
        p_row_version: input.rowVersion,
        p_won_reason: input.wonReason ?? null,
        p_lost_reason: input.lostReason ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: dealKeys.detail(vars.dealId) });
      qc.invalidateQueries({ queryKey: dealKeys.qualification(vars.dealId) });
      qc.invalidateQueries({ queryKey: dealKeys.persons(vars.dealId) });
      qc.invalidateQueries({ queryKey: dealKeys.competitors(vars.dealId) });
      qc.invalidateQueries({ queryKey: ["data_view", "manna", "deal"] }); // pipeline board
    },
  });
}

export interface QualificationUpdateInput {
  id: string;
  row_version: number;
  payload: Partial<Pick<DealInput, (typeof QUALIFICATION_FIELDS)[number]>>;
}

export function useUpdateDealQualification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: QualificationUpdateInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("manna_update_deal_qualification", {
        p_id: input.id,
        payload: input.payload,
        p_row_version: input.row_version,
      });
      if (error) throw error;
      return data as { row_version: number } | null;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: dealKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: dealKeys.qualification(variables.id) });
      // Roles + competitors live in the same view tree — keep them fresh
      // after qualification edits in case derived fields change.
      qc.invalidateQueries({ queryKey: dealKeys.persons(variables.id) });
      qc.invalidateQueries({ queryKey: dealKeys.competitors(variables.id) });
      qc.invalidateQueries({ queryKey: ["data_view", "manna", "deal"] });
    },
  });
}

// ── Deal persons (Roles section) — UI design §4.3 ───────────────────────────

export function useDealPersons(dealId: string | undefined) {
  return useQuery<DealPersonRow[]>({
    queryKey: dealKeys.persons(dealId ?? ""),
    enabled: Boolean(dealId),
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("manna_deal_persons_view")
        .select("*")
        .eq("deal_id", dealId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DealPersonRow[];
    },
  });
}

function invalidateDealPersonCaches(qc: ReturnType<typeof useQueryClient>, dealId: string) {
  qc.invalidateQueries({ queryKey: dealKeys.persons(dealId) });
  qc.invalidateQueries({ queryKey: dealKeys.qualification(dealId) });
  qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
  qc.invalidateQueries({ queryKey: ["data_view", "manna", "deal"] });
}

export interface AddDealPersonInput extends DealPersonInput {
  deal_id: string;
}

export function useAddDealPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddDealPersonInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("manna_add_deal_person", {
        payload: {
          deal_id: input.deal_id,
          person_id: input.person_id,
          role: input.role,
          is_primary_champion: input.is_primary_champion,
          notes: input.notes || null,
        },
      });
      if (error) throw error;
      return data as DealPersonRow | null;
    },
    onSuccess: (_data, variables) => invalidateDealPersonCaches(qc, variables.deal_id),
  });
}

export interface UpdateDealPersonInput {
  id: string;
  deal_id: string;
  row_version: number;
  payload: Partial<Pick<DealPersonInput, "role" | "is_primary_champion" | "notes">>;
}

export function useUpdateDealPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateDealPersonInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("manna_update_deal_person", {
        p_id: input.id,
        payload: input.payload,
        p_row_version: input.row_version,
      });
      if (error) throw error;
      return data as DealPersonRow | null;
    },
    onSuccess: (_data, variables) => invalidateDealPersonCaches(qc, variables.deal_id),
  });
}

export interface RemoveDealPersonInput {
  id: string;
  deal_id: string;
}

export function useRemoveDealPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RemoveDealPersonInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)("manna_remove_deal_person", {
        p_id: input.id,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => invalidateDealPersonCaches(qc, variables.deal_id),
  });
}

// ── Deal competitors (Competitors section) — UI design §4.4 ─────────────────

export function useDealCompetitors(dealId: string | undefined) {
  return useQuery<DealCompetitorRow[]>({
    queryKey: dealKeys.competitors(dealId ?? ""),
    enabled: Boolean(dealId),
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("manna_deal_competitors_view")
        .select("*")
        .eq("deal_id", dealId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DealCompetitorRow[];
    },
  });
}

function invalidateDealCompetitorCaches(qc: ReturnType<typeof useQueryClient>, dealId: string) {
  qc.invalidateQueries({ queryKey: dealKeys.competitors(dealId) });
  qc.invalidateQueries({ queryKey: dealKeys.qualification(dealId) });
  qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) });
  qc.invalidateQueries({ queryKey: ["data_view", "manna", "deal"] });
}

export interface AddDealCompetitorInput extends DealCompetitorInput {
  deal_id: string;
}

export function useAddDealCompetitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddDealCompetitorInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("manna_add_deal_competitor", {
        payload: {
          deal_id: input.deal_id,
          competitor_organization_id: input.competitor_organization_id,
          is_incumbent: input.is_incumbent,
          notes: input.notes || null,
        },
      });
      if (error) throw error;
      return data as DealCompetitorRow | null;
    },
    onSuccess: (_data, variables) => invalidateDealCompetitorCaches(qc, variables.deal_id),
  });
}

export interface UpdateDealCompetitorInput {
  id: string;
  deal_id: string;
  row_version: number;
  payload: Partial<Pick<DealCompetitorInput, "is_incumbent" | "notes">>;
}

export function useUpdateDealCompetitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateDealCompetitorInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("manna_update_deal_competitor", {
        p_id: input.id,
        payload: input.payload,
        p_row_version: input.row_version,
      });
      if (error) throw error;
      return data as DealCompetitorRow | null;
    },
    onSuccess: (_data, variables) => invalidateDealCompetitorCaches(qc, variables.deal_id),
  });
}

export interface RemoveDealCompetitorInput {
  id: string;
  deal_id: string;
}

export function useRemoveDealCompetitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RemoveDealCompetitorInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)("manna_remove_deal_competitor", {
        p_id: input.id,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => invalidateDealCompetitorCaches(qc, variables.deal_id),
  });
}
